import React, { useState } from "react";
import { api } from "../lib/api";
import { getSessionId, trackCTA } from "../lib/analytics";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Mail, Sparkles } from "lucide-react";

/**
 * LeadCapture — "Get a Free Opportunity Assessment" lead magnet.
 *
 * variant:
 *  - "inline"   : large card on landing page
 *  - "compact"  : small footer/sticky form
 *  - "modal"    : centered modal body (caller controls visibility)
 */
export default function LeadCapture({ variant = "inline", source = "unknown", onSubmitted }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        email: email.trim(),
        company_name: company.trim(),
        intent: "free_assessment",
        source,
        session_id: getSessionId(),
      });
      trackCTA("submit-free-assessment", { metadata: { source } });
      setDone(true);
      onSubmitted?.();
      toast.success("Got it — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`lead-capture-${variant}-success`}
        className={`${variant === "inline" ? "panel-elev p-8" : "panel p-5"} border-l-2 border-emerald-500`}
      >
        <CheckCircle2 className="w-7 h-7 text-emerald-400 mb-3" />
        <div className="tick-label text-emerald-400 mb-2">/ Request received</div>
        <h3 className="font-display font-bold text-slate-100 text-lg mb-2">
          We&apos;ll be in touch within 24 hours.
        </h3>
        <p className="text-sm text-slate-400 leading-relaxed">
          A GovBidIQ analyst will review one of your active opportunities and email you a
          complimentary preview of the Opportunity Intelligence Report.
        </p>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <form onSubmit={submit} data-testid="lead-capture-compact" className="flex flex-wrap items-center gap-2">
        <input
          data-testid="lead-email-compact"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono w-56"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-submit-compact"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />}
          Free Assessment
        </button>
      </form>
    );
  }

  // inline (large) variant
  return (
    <div data-testid="lead-capture-inline" className="panel-elev p-6 sm:p-8">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 border border-sky-500/40 bg-sky-500/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-sky-400" strokeWidth={2.2} />
        </div>
        <div>
          <div className="tick-label text-sky-400 mb-1">/ No-cost, no-commitment</div>
          <h3 className="font-display font-black text-xl sm:text-2xl text-slate-100 leading-tight">
            Get a Free Opportunity Assessment
          </h3>
        </div>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed mb-5 max-w-2xl">
        Tell us about one active opportunity. We&apos;ll run a complimentary preview of the
        Opportunity Intelligence Report — scored, with our top three Bid/No-Bid concerns — and
        send it to your inbox.
      </p>

      <form onSubmit={submit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="tick-label block mb-1.5">Work Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="lead-email-inline"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                className="w-full bg-[var(--bg-primary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm pl-10 pr-3 py-2.5 font-mono"
              />
            </div>
          </div>
          <div>
            <label className="tick-label block mb-1.5">Company (optional)</label>
            <input
              data-testid="lead-company-inline"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Federal Solutions"
              className="w-full bg-[var(--bg-primary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-submit-inline"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-5 py-3 transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
            </>
          ) : (
            <>
              Request Free Assessment <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </>
          )}
        </button>
        <p className="text-[10px] tracking-widest uppercase text-slate-500 font-mono">
          No credit card. No spam. One-time analyst follow-up.
        </p>
      </form>
    </div>
  );
}
